import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Send,
  Loader2,
  RefreshCw,
  X,
  Edit2,
  Eye,
  Globe,
  Linkedin,
  Instagram,
  Facebook,
  ArrowLeft,
  Image,
  Trash2,
  ExternalLink,
  FileEdit,
  Tag,
  Folder,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { SocialMediaPlatformSwitcher } from "../components/SocialMediaPlatformSwitcher";
import { supabase } from "@/integrations/supabase/client";
import { useWebsites } from "@/context/WebsitesContext";
import { useAppChat } from "@/context/AppChatContext";
import { useWordPress } from "@/context/WordPressContext";
import { InstagramPost } from "../components/social/InstagramPost";
import { LinkedInPost } from "../components/social/LinkedInPost";
import { canSendToWordPress, ensurePublicWordPressUrl } from "@/utils/wordpress";
import { XLogo } from "@/components/icons/XLogo";
import { TikTokLogo } from "@/components/icons/TikTokLogo";
import { TikTokPost } from "../components/social/TikTokPost";
import { FacebookPost } from "../components/social/FacebookPost";
import { XPost } from "../components/social/XPost";
import { usePostThemes } from "@/context/PostThemesContext";
import MediaLibraryModal from "../components/MediaLibraryModal";
import { InlineWysiwygEditor } from "../components/ui/inline-wysiwyg-editor";
import { ContentTag } from "../components/ui/content-tag";
import SectionLayoutViewer from "../components/SectionLayoutViewer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TEMPLATE_TYPES, TEMPLATE_TYPE_NAMES } from "@/constants/templateTypes";
import { useNavigate } from "react-router-dom";
import { useAppState } from "@/context/AppStateContext";
// import { updateContentWithChat } from '@/api/chatEndpoints';

interface PostTheme {
  id: string;
  subject_matter: string;
  post_content: string | null;
  post_digest: string | null;
  image: string | null;
  wp_post_url: string | null;
  keywords: string[] | null;
  categories: string[] | null;
  // Optional meta fields
  format_template?: string | null;
  length_tag?: string | null;
  section_layout?: any[] | null;
  element_settings?: any[] | null;
}

interface SomePost {
  id: string;
  post_theme_id: string;
  title: string;
  content: string;
  platform: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface SomeSettings {
  id: string;
  website_id: string;
  platform: string;
  tone: string;
  length: string;
  style: string;
  target_audience: string;
  call_to_action: string;
  created_at: string;
  updated_at: string;
}

interface Platform {
  key: string;
  name: string;
  icon: React.ReactNode;
}

const InstagramPreview = ({ content }: { content: string }) => (
  <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 p-4 text-white rounded-2xl">
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
          <Instagram className="h-4 w-4" />
        </div>
        <div>
          <div className="font-semibold text-sm">Your Brand</div>
          <div className="text-xs opacity-75">Sponsored</div>
        </div>
      </div>
      <div
        className="text-sm leading-relaxed"
        dangerouslySetInnerHTML={{ __html: content }}
      />
      <div className="mt-3 flex items-center gap-4 text-xs">
        <span>❤️ 0</span>
        <span>💬 0</span>
        <span>📤 0</span>
      </div>
    </div>
  </div>
);

const ContentEditor: React.FC = () => {
  const { currentRightPaneUrl } = useAppState();
  
  // Extract postThemeId from the currentRightPaneUrl (supports both /content-editor/ and /post/ formats)
  const postThemeId = currentRightPaneUrl?.match(/\/(?:content-editor|post)\/(.+)$/)?.[1];
  
  
  const { currentWebsite } = useWebsites();
  const { startEditingPost, stopEditingPost, editingPostData } = useAppChat();
  const { setCurrentRightPaneUrl } = useAppState();
  const { settings: wpSettings } = useWordPress();
  const {
    updatePostTheme,
    fetchPostThemes,
    postThemes,
    imageMap,
    fetchImagesForWebsite,
    sendToWordPress: sendThemeToWordPress,
  } = usePostThemes();
  const [postTheme, setPostTheme] = useState<PostTheme | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [currentPlatform, setCurrentPlatform] = useState<string | null>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isSendingToWP, setIsSendingToWP] = useState(false);
  const [canvasEditMode, setCanvasEditMode] = useState(false);
  const [currentSelectionHtml, setCurrentSelectionHtml] = useState<string | null>(null);

  // Handler functions
  const onRegenerateImage = async () => {
    if (!postThemeId) return;
    setIsGeneratingImage(true);
    try {
      // TODO: Implement image generation logic
      console.log('Generate image for post:', postThemeId);
      toast.success("Image generation started");
    } catch (error) {
      console.error("Error generating image:", error);
      toast.error("Failed to generate image");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const onDeleteImage = async () => {
    if (!postThemeId) return;
    try {
      const { error } = await supabase
        .from('post_themes')
        .update({ image: null })
        .eq('id', postThemeId);

      if (error) throw error;

      setPostTheme(prev => prev ? { ...prev, image: null } : null);
      toast.success("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  const onSendToWordPress = async () => {
    if (!postThemeId) return;
    setIsSendingToWP(true);
    try {
      const ok = await sendThemeToWordPress(postThemeId);
      if (!ok) return;
      toast.success("Content sent to WordPress");
    } catch (error) {
      console.error("Error sending to WordPress:", error);
      toast.error("Failed to send to WordPress");
    } finally {
      setIsSendingToWP(false);
    }
  };

  // Fetch post theme data (tolerant of not-yet-inserted record)
  useEffect(() => {
    const fetchPostTheme = async () => {
      if (!postThemeId) return;

      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from("post_themes")
          .select("*")
          .eq("id", postThemeId)
          .single();
        if (error) {
          // If not found yet, stay in loading-ish state but don't bounce the user away
          console.warn("ContentEditor: initial fetch did not find post yet; waiting for realtime insert/update", error);
          return;
        }

        if (data) {
          setPostTheme(data);
          setEditedContent(data.post_content || "");
        }
      } catch (error) {
        console.error("Error fetching post theme:", error);
        // Do not redirect away; backend might still be writing. Show spinner and let realtime update fill in.
      } finally {
        setIsLoading(false);
      }
    };

    fetchPostTheme();
  }, [postThemeId, setCurrentRightPaneUrl]);

  // Subscribe to realtime updates for streaming tags/meta and content as it is generated (INSERT + UPDATE)
  useEffect(() => {
    if (!postThemeId) return;

    const channel = supabase
      .channel(`post_theme_updates_${postThemeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'post_themes', filter: `id=eq.${postThemeId}` },
        (payload) => {
          const updated = payload.new as PostTheme;
          // Update local state incrementally to reflect streaming server updates
          console.log('ContentEditor: Realtime update received', {
            id: updated.id,
            keywords: updated.keywords?.length || 0,
            categories: updated.categories?.length || 0,
            contentLength: updated.post_content?.length || 0
          });
          setPostTheme((prev) => ({ ...(prev || updated), ...updated }));
          if (!isEditing) {
            setEditedContent(updated.post_content || "");
          }
        }
      )
      .subscribe(() => {
        // subscribed
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [postThemeId, isEditing]);

  // Update editedContent when postTheme changes
  useEffect(() => {
    if (!isEditing && postTheme) {
      setEditedContent(postTheme.post_content || "");
    }
  }, [postTheme, isEditing]);

  // Auto-start generation via direct SSE when post has no content yet
  useEffect(() => {
    if (!autoStartRef.current && postThemeId && postTheme && !postTheme.post_content) {
      autoStartRef.current = true;
      handleRegenerateWithChat();
    }
  }, [postThemeId, postTheme]);

  // Automatically start editing mode when post loads
  useEffect(() => {
    if (postTheme && postTheme.post_content) {
      
      // Start editing mode in the main chat window automatically
      startEditingPost({
        id: postTheme.id,
        title: postTheme.subject_matter,
        content: postTheme.post_content
      });
    }
  }, [postTheme, startEditingPost]);

  // Update editedContent when chat updates the content
  useEffect(() => {
    if (editingPostData && editingPostData.id === postThemeId && editingPostData.content) {
      setEditedContent(editingPostData.content);
    }
  }, [editingPostData, postThemeId]);
  
  const [platformContent, setPlatformContent] = useState<
    Record<string, string>
  >({});
  const [currentSocialPostId, setCurrentSocialPostId] = useState<string | null>(null);
  const [isLoadingSocialContent, setIsLoadingSocialContent] = useState(false);
  const [isGeneratingSocialContent, setIsGeneratingSocialContent] = useState(false);
  const [activeSettings, setActiveSettings] = useState<SomeSettings[]>([]);
  const [wpConfigured, setWpConfigured] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const editingRootRef = useRef<HTMLDivElement>(null);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [metaCollapsed, setMetaCollapsed] = React.useState(false);
  const [savingMeta, setSavingMeta] = React.useState(false);
  const autoStartRef = useRef(false);
  const [showSectionsModal, setShowSectionsModal] = React.useState(false);

  React.useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const onScroll = () => {
      setMetaCollapsed(el.scrollTop > 120);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const platforms: Platform[] = [
    {
      key: "linkedin",
      name: "LinkedIn",
      icon: <Linkedin className="h-5 w-5" />,
    },
    {
      key: "instagram",
      name: "Instagram",
      icon: <Instagram className="h-5 w-5" />,
    },
    { key: "tiktok", name: "TikTok", icon: <TikTokLogo className="h-5 w-5" /> },
    { key: "facebook", name: "Facebook", icon: <Facebook className="h-5 w-5" /> },
    { key: "x", name: "X (Twitter)", icon: <XLogo className="h-5 w-5" /> },
  ];

  const activePlatforms = platforms.filter((platform) => {
    // Check if platform is enabled in settings
    return activeSettings.some((setting) => setting.platform === platform.key);
  });

  // Check WordPress configuration using context
  useEffect(() => {
    // Use WordPress context settings to determine if configured
    const isConfigured = !!(
      wpSettings &&
      wpSettings.wp_url &&
      wpSettings.wp_username &&
      wpSettings.wp_application_password &&
      wpSettings.is_connected
    );
    
    console.log('WordPress configured status:', {
      hasSettings: !!wpSettings,
      isConnected: wpSettings?.is_connected,
      isConfigured
    });
    
    setWpConfigured(isConfigured);
  }, [wpSettings]);

  // Load platform settings
  useEffect(() => {
    const loadPlatformSettings = async () => {
      if (!currentWebsite?.id) return;

      try {
        const { data } = await supabase
          .from("some_settings")
          .select("*")
          .eq("website_id", currentWebsite.id);

        if (data) {
          setActiveSettings(data);
        }
      } catch (error) {
        console.error("Error loading platform settings:", error);
      }
    };

    loadPlatformSettings();
  }, [currentWebsite?.id]);

  // State to track social post IDs per platform
  const [socialPostIds, setSocialPostIds] = useState<Record<string, string>>({});

  // Load existing platform content
  useEffect(() => {
    const loadPlatformContent = async () => {
      if (!postThemeId) return;

      try {
        const { data } = await supabase
          .from("some_posts")
          .select("*")
          .eq("post_theme_id", postThemeId);

        if (data) {
          const contentMap: Record<string, string> = {};
          const idsMap: Record<string, string> = {};
          data.forEach((post) => {
            contentMap[post.platform] = post.content;
            idsMap[post.platform] = post.id;
          });
          setPlatformContent(contentMap);
          setSocialPostIds(idsMap);
        }
      } catch (error) {
        console.error("Error loading platform content:", error);
      }
    };

    loadPlatformContent();
  }, [postThemeId]);

  const handlePlatformChange = async (platform: string | null) => {
    // Save current edits before switching
    if (isEditing && currentPlatform !== platform) {
      await handleSaveEdit();
    }
    setCurrentPlatform(platform);
    
    // Load content for the new platform
    if (platform !== null) {
      const content = platformContent[platform];
      const socialPostId = socialPostIds[platform];
      setCurrentSocialPostId(socialPostId || null);
      setEditedContent(content || "");
    } else {
      setCurrentSocialPostId(null);
      setEditedContent(postTheme?.post_content || "");
    }
  };

  const handleGenerateSocialContent = async () => {
    if (!currentPlatform || !postThemeId || !currentWebsite) {
      toast.error("Please select a social media platform");
      return;
    }

    setIsGeneratingSocialContent(true);
    try {
      // First, ensure the post exists in some_posts table
      const { data: existingPost, error: checkError } = await supabase
        .from("some_posts")
        .select("*")
        .eq("post_theme_id", postThemeId)
        .eq("platform", currentPlatform)
        .maybeSingle();

      if (checkError) throw checkError;

      // If post doesn't exist, create it
      if (!existingPost) {
        const { error: createError } = await supabase
          .from("some_posts")
          .insert([
            {
              post_theme_id: postThemeId,
              website_id: currentWebsite.id,
              platform: currentPlatform,
              title: postTheme?.subject_matter || "",
              content: "",
              status: "pending",
            },
          ]);

        if (createError) throw createError;
      }

      // Now generate the content
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-social-content`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            post_theme_id: postThemeId,
            platform: currentPlatform,
            website_id: currentWebsite.id,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to generate content");
      }

      const result = await response.json();
      if (!result.success)
        throw new Error(result.error || "Failed to generate content");

      // Refresh the content after generation
      const { data: updatedPost, error: fetchError } = await supabase
        .from("some_posts")
        .select("*")
        .eq("post_theme_id", postThemeId)
        .eq("platform", currentPlatform)
        .single();

      if (fetchError) throw fetchError;

      setPlatformContent((prev) => ({
        ...prev,
        [currentPlatform]: updatedPost.content,
      }));
      setEditedContent(updatedPost.content);

      toast.success(`${currentPlatform} content generated successfully`);
    } catch (err) {
      console.error("Error generating social content:", err);
      toast.error("Failed to generate content");
    } finally {
      setIsGeneratingSocialContent(false);
    }
  };

  const handleSaveEdit = async () => {
    console.log("Saving content:", {
      editedContent,
      currentPlatform,
      postThemeId,
    });
    try {
      if (currentPlatform === null) {
        // Save to main post theme content
        if (postThemeId) {
          const success = await updatePostTheme(
            postThemeId,
            {
              post_content: editedContent,
            },
            false
          ); // Don't show toast here as we'll show our own

          if (!success) {
            throw new Error("Failed to update post theme");
          }

          // Refresh post themes to ensure the UI is updated
          await fetchPostThemes();
        }
        // Update local state
        setPostTheme(prev => prev ? { ...prev, post_content: editedContent } : null);
      } else if (currentSocialPostId) {
        // Update existing social post by ID
        const { error } = await supabase.from("some_posts").update({
          content: editedContent,
          updated_at: new Date().toISOString(),
        }).eq('id', currentSocialPostId);

        if (error) throw error;

        setPlatformContent((prev) => ({
          ...prev,
          [currentPlatform]: editedContent,
        }));
      } else {
        // Create new social post (upsert by post_theme_id + platform)
        const { error } = await supabase.from("some_posts").upsert({
          post_theme_id: postThemeId,
          platform: currentPlatform,
          title: postTheme?.subject_matter || "",
          content: editedContent,
          status: "pending",
          website_id: currentWebsite?.id,
        });

        if (error) throw error;

        setPlatformContent((prev) => ({
          ...prev,
          [currentPlatform]: editedContent,
        }));
      }
      setIsEditing(false);
      console.log("Content saved successfully");
      toast.success("Content saved successfully");
    } catch (err) {
      console.error("Error saving content:", err);
      toast.error("Failed to save content");
    }
  };

  // Regenerate from scratch (generate-content-v4) with direct SSE to editor canvas
  // New function to open chat editor for social posts
  const handleOpenChatEdit = () => {
    if (!postTheme) return;

    // Determine which post to edit (social post or main post)
    const targetId = currentPlatform && currentSocialPostId ? currentSocialPostId : postThemeId;
    const postType = currentPlatform ? 'social' : 'post_theme';
    
    if (!targetId) {
      toast.error("No post ID available for editing");
      return;
    }

    // Start editing mode in the main chat window
    startEditingPost({
      id: targetId,
      title: postTheme.subject_matter,
      content: editedContent,
      postType: postType,
      platform: currentPlatform || undefined
    });

    toast.success("Chat interface opened for content editing");
  };

  const handleRegenerateWithChat = async () => {
    if (!postThemeId || !postTheme?.subject_matter) {
      toast.error("Missing post data for regeneration");
      return;
    }

    try {
      setIsGeneratingContent(true);
      toast.info("Starting fresh generation…");
      console.log('ContentEditor: Starting SSE generation', {
        postThemeId,
        subject: postTheme?.subject_matter,
        websiteId: currentWebsite?.id
      });

      // Clear local editor state so streaming starts from empty, even in edit mode
      setEditedContent("");
      // If currently editing, keep edit mode but with empty content

      // Get session for auth
      const { data: sessionData } = await supabase.auth.getSession();

      const reqBody = {
        title: postTheme.subject_matter,
        website_id: currentWebsite?.id,
        is_onboarding: false,
        website_url: currentWebsite?.url || localStorage.getItem('onboardingWebsite') || '',
        format_template: postTheme.format_template || 'standard',
        length_tag: postTheme.length_tag || 'medium',
        tone_style: JSON.stringify((postTheme as any).tone_style || { tone: 'neutral' }),
        content_type: 'post',
        keywords: postTheme.keywords || [],
        section_layout: postTheme.section_layout || [],
        element_settings: postTheme.element_settings || [],
        language: 'en',
        cornerstone_content: [],
        scraped_content: [],
        postThemeId,
        passedWebsiteId: currentWebsite?.id
      };

      console.log('ContentEditor: Sending V4 request', reqBody);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-content-v4`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(sessionData?.session?.access_token ? { 'Authorization': `Bearer ${sessionData.session.access_token}` } : {}),
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify(reqBody)
      });

      console.log('ContentEditor: V4 response received', { status: response.status, ok: response.ok, headers: Object.fromEntries(response.headers.entries()) });

      if (!response.ok) {
        const text = await response.text();
        console.error('generate-content-v4 error:', text);
        throw new Error('Failed to start generation');
      }

      // Verify server is actually streaming
      const ct = response.headers.get('content-type') || '';
      const isSse = ct.includes('text/event-stream');

      if (!isSse) {
        console.warn('ContentEditor: Server did not return SSE. Content-Type:', ct);
        toast.info('Generation started (no live stream). The content will appear once saved.');
        // Kick off a delayed refresh to pick up DB writes
        setTimeout(() => { fetchPostThemes(); }, 2000);
        return;
      }

      // Directly consume SSE and render deltas into the editor canvas
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No SSE response body');
      }

      let partial = '';
      // Always start streaming from empty buffer to avoid appending to previous content
      let full = '';
      const decoder = new TextDecoder();
      let receivedDelta = false;
      let chunkCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        partial += decoder.decode(value, { stream: true });
        const segments = partial.split('\n\n');
        partial = segments.pop() || '';
        for (const segment of segments) {
          const line = segment.trim();
          if (!line.startsWith('data:')) continue;
          const dataStr = line.replace(/^data:\s*/, '');
          if (dataStr === '[DONE]') continue;
          try {
            const evt = JSON.parse(dataStr);
            if (evt.type === 'content' && typeof evt.content === 'string') {
              full += evt.content;
              receivedDelta = true;
              chunkCount += 1;
              if (chunkCount % 10 === 1) {
                console.log('ContentEditor: SSE delta', { chunkCount, deltaLen: evt.content.length, totalLen: full.length });
              }
              setEditedContent(full);
            }
          } catch {
            // ignore malformed keep-alive lines
          }
        }
      }

      // Final refresh from DB in case last delta persisted after loop
      await fetchPostThemes();
      console.log('ContentEditor: SSE complete', { receivedDelta, finalLength: full.length, chunks: chunkCount });
      if (receivedDelta) toast.success('Streaming complete');
    } catch (error) {
      console.error("Regenerate error:", error);
      toast.error("Failed to start generation");
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const getPlatformIcon = (platform: string | null) => {
    switch (platform) {
      case "linkedin":
        return <Linkedin className="h-4 w-4" />;
      case "instagram":
        return <Instagram className="h-4 w-4" />;
      case "facebook":
        return <Facebook className="h-4 w-4" />;
      case "tiktok":
        return <TikTokLogo className="h-4 w-4" />;
      case "x":
        return <XLogo className="h-4 w-4" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  const formatSocialContent = (content: string, platform: string) => {
    // Basic formatting for social platforms
    return content.replace(/\n/g, "<br>");
  };

  const getPlatformActions = (platform: string | null) => {
    const actions: React.ReactNode[] = [];

    if (platform === null) {
      // Website content actions
      if (isGeneratingContent) {
        actions.push(
          <Button
            key="regenerate"
            variant="ghost"
            size="icon"
            onClick={handleRegenerateWithChat}
            disabled={isGeneratingContent}
            title="Restart chat editing"
          >
            {isGeneratingContent ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        );
      } else {
        actions.push(
          <Button
            key="generate"
            variant="ghost"
            size="icon"
            onClick={handleRegenerateWithChat}
            disabled={isGeneratingContent}
            title="Restart chat editing"
          >
            <FileEdit className="h-4 w-4" />
          </Button>
        );
      }

      // Image generation for website content
      actions.push(
        <Button
          key="generate-image"
          variant="ghost"
          size="icon"
          onClick={onRegenerateImage}
          disabled={isGeneratingImage}
          className={`h-8 w-8 ${
            isGeneratingImage
              ? "text-purple-300 cursor-not-allowed"
              : "text-purple-600 hover:bg-purple-100 hover:text-purple-700"
          }`}
          title="Generate image"
        >
          {isGeneratingImage ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Image className="h-4 w-4" />
          )}
        </Button>
      );

      // WordPress publish button
      if (wpConfigured && canSendToWordPress) {
        actions.push(
          <Button
            key="publish-wp"
            variant="ghost"
            size="icon"
            onClick={onSendToWordPress}
            disabled={isSendingToWP}
            className={`h-8 w-8 ${
              isSendingToWP
                ? "text-blue-300 cursor-not-allowed"
                : "text-blue-600 hover:bg-blue-100 hover:text-blue-700"
            }`}
            title="Publish to WordPress"
          >
            {isSendingToWP ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
          </Button>
        );
      }
    } else {
      // Social platform actions
      if (editedContent) {
        // Regenerate button for existing social content
        actions.push(
          <Button
            key="regenerate-social"
            variant="ghost"
            size="icon"
            onClick={handleGenerateSocialContent}
            disabled={isGeneratingSocialContent}
            title={`Regenerate ${platform} content`}
          >
            {isGeneratingSocialContent ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        );
        
        // Chat edit button for social content
        actions.push(
          <Button
            key="chat-edit-social"
            variant="ghost"
            size="icon"
            onClick={handleOpenChatEdit}
            title={`Edit ${platform} content with chat`}
          >
            <FileEdit className="h-4 w-4" />
          </Button>
        );
      }
    }

    return actions;
  };

  const handleSelectionToChat = () => {
    if (!postThemeId || !postTheme) return;
    const selection = window.getSelection();
    const selectedHtml = selection && selection.rangeCount > 0
      ? selection.getRangeAt(0).cloneContents()
      : null;
    let htmlString: string | null = null;
    if (selectedHtml) {
      const container = document.createElement('div');
      container.appendChild(selectedHtml);
      htmlString = container.innerHTML;
    }
    const selectionText = selection?.toString() || '';
    const context = htmlString || selectionText || '';
    if (!context) return;
    startEditingPost({
      id: postTheme.id,
      title: postTheme.subject_matter,
      content: postTheme.post_content || editedContent,
      selection: context
    } as any);
  };

  // Capture selection while editing (InlineWysiwygEditor) and stash for chat
  useEffect(() => {
    if (!isEditing) return;
    const root = editingRootRef.current;
    const getSelectionText = () => {
      try {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const text = selection.toString().trim();
        if (!text) return;

        // Ensure selection is inside the editor's contentEditable
        const ce = root?.querySelector('div[contenteditable="true"]');
        if (ce && selection.anchorNode) {
          let node: Node | null = selection.anchorNode;
          let inside = false;
          while (node) {
            if (node === ce) { inside = true; break; }
            node = node.parentNode;
          }
          if (!inside) return;
        }

        try {
          localStorage.setItem('selected_excerpt', text);
          localStorage.setItem('selected_excerpt_for_chat', text);
        } catch {}
        try {
          window.dispatchEvent(new CustomEvent('content_edit_selection', { detail: { text } }));
        } catch {}
        toast.message('Text selected for chat editing');
      } catch {}
    };

    const onMouseUp = () => setTimeout(getSelectionText, 0);
    const onKeyUp = () => setTimeout(getSelectionText, 0);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [isEditing]);

  const handlePreviewSelection = () => {
    try {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const text = selection.toString().trim();
      if (!text) return;

      // Ensure the selection started within the preview content
      const container = contentRef.current;
      if (container && selection.anchorNode) {
        let node: Node | null = selection.anchorNode;
        let inside = false;
        while (node) {
          if (node === container) { inside = true; break; }
          node = node.parentNode;
        }
        if (!inside) return;
      }

      try {
        localStorage.setItem('selected_excerpt', text);
        localStorage.setItem('selected_excerpt_for_chat', text);
      } catch {}
      try {
        window.dispatchEvent(new CustomEvent('content_edit_selection', { detail: { text } }));
      } catch {}
      toast.message('Text selected for chat editing');
    } catch {}
  };

  // Find current post theme for image display
  const currentPostTheme = useMemo(() => {
    return postThemes.find((theme) => theme.id === postThemeId);
  }, [postThemes, postThemeId]);

  // Determine which image to show
  const imageToShow = useMemo(() => {
    if (currentPlatform === "instagram" || currentPlatform === "facebook") {
      // For social platforms, show the post theme image if available
      return postTheme?.image ? { url: postTheme.image, name: "Post Image" } : null;
    }

    // For website content, show the post theme image
    if (postTheme?.image) {
      return { url: postTheme.image, name: "Post Image" };
    }

    return null;
  }, [currentPlatform, postTheme?.image]);

  const handleSelectImage = async (image: { id: string; url: string; name: string; description?: string }) => {
    if (!postThemeId) return;

    try {
      // Update the post theme with the selected image
      const { error } = await supabase
        .from("post_themes")
        .update({ image: image.id })
        .eq("id", postThemeId);

      if (error) throw error;

      // Update local state
      setPostTheme(prev => prev ? { ...prev, image: image.id } : null);
      toast.success("Image selected successfully");
    } catch (error) {
      console.error("Error selecting image:", error);
      toast.error("Failed to select image");
    }
  };

  useEffect(() => {
    // image debug removed
  }, [imageToShow]);

  // Debug logging for image display issues
  // quiet debug

  // Always fetch images for the current website when page loads or website changes
  useEffect(() => {
    if (currentWebsite?.id) {
      fetchImagesForWebsite(currentWebsite.id);
    }
  }, [currentWebsite?.id]);

  // Cleanup: stop editing mode when component unmounts
  useEffect(() => {
    return () => {
      stopEditingPost();
    };
  }, [stopEditingPost]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading post data...</span>
        </div>
      </div>
    );
  }

  if (!postTheme) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center p-8">
          <div className="max-w-md">
            <h3 className="text-xl font-semibold mb-4">Generating your article</h3>
            <p className="text-muted-foreground mb-6">
              We’re preparing your post. Tags and details will appear first, followed by the full article as it streams in.
            </p>
            <div className="p-4 bg-muted/50 rounded-lg border">
              <p className="text-sm text-muted-foreground">🌱 Sit tight — content will appear here automatically.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  
  

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentRightPaneUrl("/planned-content")}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-medium">{postTheme?.subject_matter || "Loading..."}</h3>
        </div>
        <div className="flex items-center gap-2">
          {currentPlatform !== null && !editedContent && !isGeneratingSocialContent && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateSocialContent}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Generate {currentPlatform} content
            </Button>
          )}
          {currentPlatform !== null && editedContent && !isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleGenerateSocialContent}
              disabled={isGeneratingSocialContent}
              className="gap-2"
            >
              {isGeneratingSocialContent ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Regenerate
            </Button>
          )}
          {!isEditing && currentPlatform !== null && editedContent && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenChatEdit}
                className="gap-2"
              >
                <FileEdit className="h-4 w-4" />
                Chat Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="gap-2"
              >
                <Edit2 className="h-4 w-4" />
                Edit manually
              </Button>
            </>
          )}
          <Button variant="ghost" size="icon" onClick={() => setCurrentRightPaneUrl("/planned-content")}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header removed intentionally */}

          {/* Content Area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Editor/Preview */}
          <div ref={scrollAreaRef} className="flex-1 p-4 overflow-auto">
            {/* Meta section (non-sticky): meta tags and regenerate, then keywords, then image */}
            {postTheme && (
              <div className="mb-4 px-1">
                <div className="flex flex-col gap-3">
                  {/* Template/Sections/Length + Regenerate */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-2 items-center">
                      {/* Editable Template Type - styled pill with dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={cn(
                              "h-6 rounded-full border px-2 text-[10px] inline-flex items-center gap-1",
                              postTheme.format_template ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-muted/40 text-muted-foreground border-muted"
                            )}
                          >
                            <span className="inline-flex h-3 w-3 items-center justify-center">
                              <span className="h-2 w-2 rounded-full bg-blue-500" />
                            </span>
                            {postTheme.format_template ? TEMPLATE_TYPE_NAMES[postTheme.format_template as keyof typeof TEMPLATE_TYPE_NAMES] : 'type'}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-40">
                          {Object.values(TEMPLATE_TYPES).map((t) => (
                            <DropdownMenuItem
                              key={t}
                              onClick={async () => {
                                const val = t as string;
                                if (!postThemeId) return;
                                setSavingMeta(true);
                                try {
                                  const { error } = await supabase
                                    .from('post_themes')
                                    .update({ format_template: val })
                                    .eq('id', postThemeId);
                                  if (error) throw error;
                                  setPostTheme(prev => prev ? { ...prev, format_template: val } : prev);
                                  toast.success('Template updated');
                                } catch (e) {
                                  console.error('Update template error', e);
                                  toast.error('Failed to update template');
                                } finally {
                                  setSavingMeta(false);
                                }
                              }}
                            >
                              {TEMPLATE_TYPE_NAMES[t]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {/* Sections (read-only count) */}
                      {Array.isArray(postTheme.section_layout) && (
                        <button
                          type="button"
                          className="px-1.5 py-0.5 text-[10px] rounded-full border inline-flex items-center gap-0.5 font-normal bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                          onClick={() => setShowSectionsModal(true)}
                          title="View proposed structure"
                        >
                          <span className="inline-flex h-3 w-3 items-center justify-center">
                            <span className="h-2 w-2 rounded-full bg-blue-500" />
                          </span>
                          sections: {postTheme.section_layout.length}
                        </button>
                      )}

                      {/* Editable Length Tag - styled pill with dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={cn(
                              "h-6 rounded-full border px-2 text-[10px] inline-flex items-center gap-1",
                              postTheme.length_tag ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-muted/40 text-muted-foreground border-muted"
                            )}
                          >
                            <span className="inline-flex h-3 w-3 items-center justify-center">
                              <span className="h-2 w-2 rounded-full bg-purple-500" />
                            </span>
                            {postTheme.length_tag || 'length'}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-36">
                          {['ultra-short','short','medium','medium-long','long','very-long'].map((len) => (
                            <DropdownMenuItem
                              key={len}
                              onClick={async () => {
                                const val = len;
                                if (!postThemeId) return;
                                setSavingMeta(true);
                                try {
                                  const { error } = await supabase
                                    .from('post_themes')
                                    .update({ length_tag: val })
                                    .eq('id', postThemeId);
                                  if (error) throw error;
                                  setPostTheme(prev => prev ? { ...prev, length_tag: val } : prev);
                                  toast.success('Length updated');
                                } catch (e) {
                                  console.error('Update length error', e);
                                  toast.error('Failed to update length');
                                } finally {
                                  setSavingMeta(false);
                                }
                              }}
                            >
                              {len}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex items-center">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleRegenerateWithChat}
                        disabled={isGeneratingContent}
                        className="h-8 w-8"
                        title="Regenerate"
                      >
                        {isGeneratingContent ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Keywords/Categories */}
                  <div className="flex flex-wrap gap-1">
                    {postTheme.categories?.map((category, index) => (
                      <ContentTag key={`c-${index}`} type="category" label={category} />
                    ))}
                    {postTheme.keywords?.map((keyword, index) => (
                      <ContentTag key={`k-${index}`} type="keyword" label={keyword} />
                    ))}
                  </div>
                  {/* Image selector/preview */}
                  <div className="flex items-center gap-3">
                    {imageToShow ? (
                      <img src={imageToShow.url} alt={imageToShow.name || 'Selected image'} className="rounded h-16 w-16 object-cover" />
                    ) : (
                      <div className="flex items-center justify-center rounded border border-dashed border-muted text-muted-foreground h-16 w-16 text-xs">
                        <Image className="h-4 w-4" />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setIsMediaLibraryOpen(true)}>Select from Library</Button>
                      {onRegenerateImage && (
                        <Button variant="outline" size="sm" onClick={onRegenerateImage} disabled={isGeneratingImage}>
                          {isGeneratingImage ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Image className="mr-2 h-4 w-4" />
                          )}
                          Generate Image
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
              {!editedContent?.trim() && currentPlatform === null ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <div className="max-w-md">
                    <h3 className="text-xl font-semibold mb-4">
                      Generating your article
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      We're preparing your post. Tags and details will appear first, followed by the full article as it streams in.
                    </p>
                    <div className="p-4 bg-muted/50 rounded-lg border">
                      <p className="text-sm text-muted-foreground">
                        🌱 Sit tight — content will appear here automatically.
                      </p>
                    </div>
                  </div>
                </div>
              ) : !editedContent?.trim() && currentPlatform !== null ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-8">
                  <div className="max-w-md">
                    <h3 className="text-xl font-semibold mb-4">
                      No {currentPlatform} content yet
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Generate social media content optimised for {currentPlatform} based on your main article.
                    </p>
                    <Button
                      onClick={handleGenerateSocialContent}
                      disabled={isGeneratingSocialContent}
                      size="lg"
                      className="gap-2"
                    >
                      {isGeneratingSocialContent ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-5 w-5" />
                          Generate {currentPlatform} content
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="h-full flex flex-col">

                  {isEditing ? (
                    <div className="h-full flex flex-col" ref={editingRootRef}>
                      <InlineWysiwygEditor
                        content={editedContent}
                        onChange={(html) => {
                          console.log("InlineWysiwygEditor onChange:", html);
                          setEditedContent(html);
                        }}
                        onSave={handleSaveEdit}
                        onCancel={() => {
                          setIsEditing(false);
                          setEditedContent(postTheme?.post_content || "");
                        }}
                        className="flex-1"
                      />
                    </div>
                  ) : (
                    <div className="p-4">
                        {currentPlatform === "instagram" ? (
                          <div className="bg-white">
                            <InstagramPost content={editedContent} />
                          </div>
                        ) : currentPlatform === "linkedin" ? (
                          <div className="bg-white">
                            <LinkedInPost
                              content={editedContent}
                              websiteUrl={currentWebsite?.url}
                              websiteName={currentWebsite?.name}
                            />
                          </div>
                        ) : currentPlatform === "facebook" ? (
                          <div className="bg-white">
                            <FacebookPost
                              content={editedContent}
                              websiteName={currentWebsite?.name}
                            />
                          </div>
                        ) : currentPlatform === "tiktok" ? (
                          <div className="phone-frame">
                            <TikTokPost
                              content={editedContent || postTheme?.post_content || ""}
                              websiteName={currentWebsite?.name}
                            />
                          </div>
                        ) : currentPlatform === "x" ? (
                          <XPost
                            content={editedContent}
                            websiteName={currentWebsite?.name}
                          />
                        ) : (
                          <div
                            ref={contentRef}
                            onMouseUp={handlePreviewSelection}
                            onKeyUp={handlePreviewSelection}
                            onClick={() => {
                              if (!isGeneratingContent) {
                                setIsEditing(true);
                              }
                            }}
                            className={cn(
                              "prose max-w-none select-text cursor-pointer",
                              "hover:bg-muted/20 transition-colors rounded-lg p-4",
                              "border border-transparent hover:border-brand-green-border/50",
                              currentPlatform && "social-content",
                              currentPlatform === "tiktok" &&
                                "tiktok-content",
                              currentPlatform === "facebook" &&
                                "facebook-content"
                            )}
                            dangerouslySetInnerHTML={{
                              __html: currentPlatform
                                ? formatSocialContent(
                                    editedContent,
                                    currentPlatform
                                  )
                                : editedContent,
                            }}
                            title="Click to edit content"
                          />
                        )}
                      </div>
                    
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t">
        <div className="flex items-center justify-between">
          {/* Bottom horizontal platform bar on the left */}
          <div className="flex items-center gap-2">
            <Button
              variant={currentPlatform === null ? "default" : "ghost"}
              size="icon"
              onClick={() => handlePlatformChange(null)}
              className="h-10 w-10"
              title="Website Content"
            >
              <Globe className="h-5 w-5" />
            </Button>
            {activePlatforms.map((platform) => (
              <Button
                key={platform.key}
                variant={currentPlatform === platform.key ? "default" : "ghost"}
                size="icon"
                onClick={() => handlePlatformChange(platform.key)}
                className="h-10 w-10"
                title={platform.name}
              >
                {platform.icon}
              </Button>
            ))}
          </div>

          {/* WordPress action on the right */}
          <div className="flex items-center gap-2">
            {currentWebsite && currentPlatform === null && (
              wpConfigured ? (
                <>
                  {postTheme?.wp_post_url ? (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          const publicUrl = ensurePublicWordPressUrl(postTheme.wp_post_url, currentWebsite?.url);
                          if (publicUrl) {
                            window.open(publicUrl, '_blank');
                          }
                        }}
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        View in WordPress
                      </Button>
                      <Button 
                        variant="ghost"
                        size="icon"
                        disabled
                        title="Already sent to WordPress"
                        className="text-green-600"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Button 
                      onClick={onSendToWordPress} 
                      disabled={isSendingToWP || !postTheme?.post_content}
                      title={
                        !postTheme?.post_content 
                          ? "No content to send" 
                          : "Send this post to WordPress"
                      }
                      className="gap-2"
                    >
                      {isSendingToWP ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Sending to WordPress
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Send to WordPress
                        </>
                      )}
                    </Button>
                  )}
                </>
              ) : (
                <Button variant="outline" onClick={() => setCurrentRightPaneUrl('/settings')}>
                  Connect WordPress
                </Button>
              )
            )}
          </div>
        </div>
      </div>

      <MediaLibraryModal
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelectImage={handleSelectImage}
        websiteId={currentWebsite?.id || ""}
      />

      {Array.isArray(postTheme?.section_layout) && (
        <SectionLayoutViewer
          isOpen={showSectionsModal}
          onClose={() => setShowSectionsModal(false)}
          sectionIds={postTheme.section_layout as unknown as string[]}
        />
      )}
    </div>
  );
};

export default ContentEditor;
